import { LotEventTypes, LotStatus } from '@parallel-fluidics/shared/constants';
import { lotEventToFirestore, lotFromFirestore } from '@parallel-fluidics/shared/models/lots';
import {
  collection,
  doc,
  getFirestore,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';

/**
 * Set a lot's status (Mark empty, Quarantine, ...) and log a
 * `status_changed` event in the same transaction. The event's reason
 * records the new status plus the operator's note, so the Activity log
 * reads "Quarantined — dropped bag on floor".
 *
 * Marking Empty also zeroes remainingQty (with the delta on the event),
 * so the advisory count can never contradict the status — and a later
 * positive adjustment can cleanly reopen the bag from zero.
 *
 * Quarantined is sticky: a fresh read guards against any transition off
 * it (e.g. a Mark empty racing a quarantine), since Empty bags are
 * consumable again while Quarantined ones are rejected at scan.
 */
export default async function updateLotStatus({
  lotId, status, reason = null, user,
}) {
  const db = getFirestore();
  const lotRef = doc(db, 'lots', lotId);

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(lotRef);
    if (!snap.exists()) throw new Error('Lot not found');
    const lot = lotFromFirestore(snap);
    if (lot.status === LotStatus.QUARANTINED && status !== LotStatus.QUARANTINED) {
      throw new Error(`${lot.code} is Quarantined — quarantined lots keep their status`);
    }

    const patch = { status, statusChangedAt: serverTimestamp() };
    const zeroed = status === LotStatus.EMPTY && (lot.remainingQty ?? 0) !== 0;
    if (zeroed) patch.remainingQty = 0;
    transaction.update(lotRef, patch);

    transaction.set(doc(collection(lotRef, 'events')), lotEventToFirestore({
      type: LotEventTypes.STATUS_CHANGED,
      deltaQty: zeroed ? -lot.remainingQty : null,
      reason: [status, reason].filter(Boolean).join(' — '),
      _createdBy: user,
    }, serverTimestamp));
  });
}
